import InnerHtml from '@/services/contentful/utils/innerHtml'
import WithinPageNavigation from '@/components/within-page-nav'
import {
  ButtonLinkPrimaryTextAndArrow,
  ButtonPrimary,
} from '@/components/button'
import { VacancyDetail } from '@/interfaces/vacancy'
import { Directions } from '@/lib/constants'
import Image from '@/components/image'
import Block from '@/layouts/block'
import Head from 'next/head'

type VacancyDetailInfoProps = Pick<VacancyDetail, 'content'> & {
  activeElementIndex: number
  bannerImage: {
    src: string
    title: string
    description: string
  }
  handleChangeActiveElemIndex: (index: number) => void
  handleWithinPageNav: (d: Directions) => void
  sollicitatiePath: string
}

interface WithinPageNavBtnProps {
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  content: string
}

const WithinPageNavBtn = ({ handleClick, content }: WithinPageNavBtnProps) => (
  <ButtonPrimary className="min-w-[220px] w-full" onClick={handleClick}>
    {content}
  </ButtonPrimary>
)

const VacancyDetailInfo = ({
  content,
  bannerImage,
  activeElementIndex,
  handleChangeActiveElemIndex,
  handleWithinPageNav,
  sollicitatiePath,
}: VacancyDetailInfoProps) => {
  const vacancyDetailContentId = 'vacancyDetailContent'

  // We need the user to scroll up to the content
  const goToContent = () => {
    const element = document.getElementById(vacancyDetailContentId)

    if (element) {
      const { top } = element.getBoundingClientRect()
      const additionalOffset = 80 // we need to add the height of the header and some additional space (because the header is position: fixed; thus taking it out of the flow)
      window.scrollTo({
        top: top + window.scrollY - additionalOffset,
        behavior: 'smooth',
      })
    }
  }

  const handleSelectActiveElem = (i: number) => {
    handleChangeActiveElemIndex(i)
    goToContent()
  }

  const handleClickPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    handleWithinPageNav(Directions.PREV)
    goToContent()
  }

  const handleClickNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    handleWithinPageNav(Directions.NEXT)
    goToContent()
  }

  const displayPrevBtn = activeElementIndex > 0
  const displayNextBtn = activeElementIndex + 1 < content.length

  return (
    <>
      <Block
        id={vacancyDetailContentId}
        className="rounded-t-sm col-span-full lg:col-start-5 lg:col-end-13 lg:row-start-4 lg:row-span-4"
      >
        <Image
          className="vacancyDetailBannerImg"
          src={bannerImage.src || ''}
          alt={bannerImage.title || ''}
          width="836"
          height="268"
        />
        <InnerHtml
          className="vacancyDetailContent mb-10 p-10"
          innerHtml={content[activeElementIndex]?.description || ''}
        />
        <WithinPageNavigation
          className="w-full mx-auto max-w-[475px]"
          activeElementIndex={activeElementIndex}
          handleChangeActiveElemIndex={handleSelectActiveElem}
          numOfItems={content.length}
          prevBtn={
            displayPrevBtn && (
              <WithinPageNavBtn
                handleClick={handleClickPrev}
                content={content[activeElementIndex - 1].title}
              />
            )
          }
          nextBtn={
            displayNextBtn ? (
              <WithinPageNavBtn
                handleClick={handleClickNext}
                content={content[activeElementIndex + 1].title}
              />
            ) : (
              <ButtonLinkPrimaryTextAndArrow
                version="c"
                href={sollicitatiePath}
                className="min-w-[220px] w-full"
              >
                Solliciteren
              </ButtonLinkPrimaryTextAndArrow>
            )
          }
        />
      </Block>
    </>
  )
}

export default VacancyDetailInfo
